import { useNavigate, useParams } from 'react-router-dom';
import { Boxes, Network, Layers, Plus, ArrowRight } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { EmptyState, StatusPill, type StatusTone } from '@vnetwork/ui/data';
import { Button, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { useClusters, useK8sNetworks, useK8sResources, type K8sCluster } from '@/api/k8s';
import { ListPageShell, ErrorState } from '../compute/components/ListPageShell';
import styles from '../Services.module.css';

/** Maps a cluster status string to a StatusPill tone. Matches ClusterList.tsx. */
function clusterTone(s?: string): StatusTone {
  const v = String(s ?? '').toLowerCase();
  if (/active|running|ready/.test(v)) return 'success';
  if (/pend|deploy|creat|provision/.test(v)) return 'warning';
  if (/error|fail/.test(v)) return 'danger';
  return 'muted';
}

/** Capitalize a raw status string for display ("active" → "Active"). */
const titleCase = (s?: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Kubernetes service landing page: at-a-glance counters + active clusters + CTA. */
export function KubernetesDashboard() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `/services/kubernetes/${serviceId}`;
  const { data: clusters = [], isLoading, isError, refetch } = useClusters(serviceId);
  const { data: networks = [] } = useK8sNetworks(serviceId);
  const { data: resources = [] } = useK8sResources(serviceId);

  const counters = [
    { icon: Boxes, label: 'Clusters', value: clusters.length, to: `${base}/clusters` },
    { icon: Network, label: 'Networks', value: networks.length, to: `${base}/networks` },
    { icon: Layers, label: 'Resources', value: resources.length, to: `${base}/resources` },
  ];

  return (
    <ListPageShell icon={<Boxes size={22} />} title="Kubernetes" description="Managed Kubernetes clusters, networks and resources.">
      {isError ? (
        <ErrorState title="Couldn't load the dashboard" description="The Kubernetes service overview is temporarily unavailable." onRetry={() => refetch()} />
      ) : isLoading ? (
        <Skeleton style={{ height: 320 }} />
      ) : (
      <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--vn-space-3)', marginBottom: 'var(--vn-space-4)' }}>
        {counters.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.label} flat>
              <CardBody>
                <button type="button" onClick={() => navigate(c.to)} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', font: 'inherit', color: 'var(--vn-fg-default)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--vn-fg-muted)', marginBottom: 6, fontSize: 'var(--vn-font-size-sm)' }}><Icon size={16} /> {c.label}</div>
                  <div style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 700 }}>{c.value}</div>
                </button>
              </CardBody>
            </Card>
          );
        })}
      </div>

      <Section
        title="Clusters"
        description="Your managed Kubernetes clusters."
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
            <Button startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/clusters/create`)}>Create cluster</Button>
            <Button variant="secondary" endIcon={<ArrowRight size={15} />} onClick={() => navigate(`${base}/clusters`)}>View all</Button>
          </div>
        }
      >
        <Card>
          <CardBody>
            {clusters.length === 0 ? (
              <EmptyState icon={<Boxes size={20} />} title="No clusters yet" description="Create a cluster to get started." />
            ) : (
              <div className={styles.grid}>
                {clusters.map((c: K8sCluster) => (
              <button
                key={c.id}
                type="button"
                className={`${styles.card} ${styles.cardClickable}`}
                onClick={() => navigate(`${base}/clusters/${c.id}`)}
              >
                <div className={styles.cardHead}>
                  <span className={`${styles.cardIcon} ${styles.cardIconActive}`}><Boxes size={19} /></span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className={styles.cardTitle}>{c.name || c.id}</div>
                    <div className={styles.cardSub}>
                      {c.version?.gitVersion || c.metadata?.version || '—'}{c.metadata?.pools?.length ? ` · ${c.metadata.pools.length} pool${c.metadata.pools.length === 1 ? '' : 's'}` : ''}
                    </div>
                  </div>
                  <StatusPill tone={clusterTone(c.status ?? c.state)}>{titleCase(c.status || c.state) || '—'}</StatusPill>
                </div>
                <div className={styles.cardFoot}>
                  <span className={styles.cardSub} />
                  <span className={styles.openHint}>Open <ArrowRight size={14} /></span>
                </div>
              </button>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </Section>
      </>
      )}
    </ListPageShell>
  );
}
